import { Button, EchartsWithTheme, Empty, FormTitle, Modal, useTheme } from '@maxtropy/components';
import dayjs, { Dayjs } from 'dayjs';
import React, { useContext, useMemo, useState } from 'react';
import { getChartOption, getCostTrendChartOption, getPieChartOption } from './charts';
import styles from './index.module.scss';
import { useRequest } from 'ahooks';
import {
  apiV2EnergyReportCostDetailPiePost,
  apiV2EnergyReportCostDetailPost,
  apiV2EnergyReportTrendPost,
} from '@maxtropy/device-customer-apis-v2';
import { buildMonthCycleParams, getTimeRangeByMonthPeriod } from '../../utils';
import CardBox from '../CardBox';
import { formatThousandUnitDefaultTwo, keepPercentOrLine } from '@/utils/utils';
import { formatName } from '@/pages/WgqAllMaps/utils';
import { DateType } from '../../const';
import { MonthPeriodContext } from '../..';

type RangeValue = [Dayjs | null, Dayjs | null] | null;

const TOTAL = 1;

const color = ['#2D8DFF', '#16DD8E', '#FF477B', '#FF9247', '#52E7FF', '#FFCB47', '#854ECA', '#ABD335', '#FF6D2C'];

interface CostProps {
  timeResolution: DateType;
  dateValue?: RangeValue;
}

const Cost: React.FC<CostProps> = ({ timeResolution, dateValue }) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);
  const { reportScope, monthPeriodType, monthPeriodStart, monthPeriodEnd, monthCycleApiParams } =
    useContext(MonthPeriodContext);

  // 总成本详情
  const { data: costData, loading } = useRequest(
    async () => {
      let fromTime: number;
      let toTime: number;

      if (timeResolution === DateType.DAY) {
        // 按日查询：直接使用用户选择的日期范围
        fromTime = dateValue?.[0]?.startOf('day').valueOf() ?? 0;
        toTime = dateValue?.[1]?.endOf('day').valueOf() ?? 0;
      } else {
        // 按月/按年查询：使用月周期计算
        const timeRange = getTimeRangeByMonthPeriod(
          timeResolution!,
          dateValue?.[0],
          dateValue?.[1],
          monthPeriodType,
          monthPeriodStart,
          monthPeriodEnd
        );
        fromTime = timeRange.startTime;
        toTime = timeRange.endTime;
      }

      return await apiV2EnergyReportCostDetailPost({
        fromTime,
        toTime,
        timeResolution: timeResolution,
        ...buildMonthCycleParams(timeResolution, monthCycleApiParams, reportScope),
      });
    },
    {
      ready: !!timeResolution && !!dateValue,
      refreshDeps: [timeResolution, dateValue, reportScope, monthCycleApiParams],
      debounceWait: 100,
    }
  );
  // 历史成本趋势
  const { data: costTrendData, loading: trendLoading } = useRequest(
    async () => {
      const endDate = dayjs(dateValue?.[1]);
      const startDate = endDate.subtract(2, 'year').startOf('year');

      return await apiV2EnergyReportTrendPost({
        type: TOTAL,
        fromTime: startDate.valueOf(),
        toTime: endDate.endOf('year').valueOf(),
        timeResolution: 'DAY',
        deptOrArea: reportScope === 'dept' ? 1 : reportScope === 'area' ? 2 : undefined,
        deptAreaId: monthCycleApiParams?.deptAreaId,
        ...buildMonthCycleParams(DateType.DAY, monthCycleApiParams, reportScope),
      });
    },
    {
      ready: open && !!timeResolution && !!dateValue && !!dateValue?.[1],
      refreshDeps: [timeResolution, dateValue, open],
      debounceWait: 100,
    }
  );
  // 历史成本趋势 (饼图)
  const { data: costTrendPieData, loading: trendPieLoading } = useRequest(
    async () => {
      const endDate = dayjs(dateValue?.[1]);
      const startDate = endDate.subtract(2, 'year').startOf('year');

      return await apiV2EnergyReportCostDetailPiePost({
        fromTime: startDate.valueOf(),
        toTime: endDate.endOf('year').valueOf(),
        timeResolution: 'Y',
        deptOrArea: reportScope === 'dept' ? 1 : reportScope === 'area' ? 2 : undefined,
        deptAreaId: monthCycleApiParams?.deptAreaId,
        ...buildMonthCycleParams(DateType.YEAR, monthCycleApiParams, reportScope),
      });
    },
    {
      ready: open && !!timeResolution && !!dateValue && !!dateValue?.[1],
      refreshDeps: [timeResolution, dateValue, open],
      debounceWait: 100,
    }
  );

  // 图表
  const costChartOption = useMemo(() => {
    return getChartOption(costData, timeResolution);
  }, [costData, timeResolution]);

  // 历史趋势图表
  const costTrendChartOption = useMemo(() => {
    return getCostTrendChartOption(costTrendData);
  }, [costTrendData]);

  return (
    <>
      <CardBox
        title="总成本详情"
        extraContent={
          <div className={styles.time_box}>
            <Button type="primary" onClick={() => setOpen(true)}>
              历史成本趋势对比
            </Button>
          </div>
        }
      >
        <div className={styles.chart_wrapper}>
          <EchartsWithTheme
            empty={{
              isShowEmpty: !costData || costData?.list?.length === 0,
              emptyNode: <Empty />,
            }}
            loading={loading}
            theme={theme}
            style={{ height: 300 }}
            option={costChartOption}
          />
        </div>
      </CardBox>
      <Modal
        title="历史成本趋势对比"
        open={open}
        width="80%"
        mask={false}
        style={{ top: '10%', minWidth: 900 }}
        onOk={() => {}}
        footer={null}
        onCancel={() => {
          setOpen(false);
        }}
      >
        <div style={{ marginTop: 16, height: 350, width: '100%' }}>
          <EchartsWithTheme
            theme={theme}
            loading={trendLoading}
            empty={{
              isShowEmpty: !costTrendData || costTrendData.list?.length === 0,
              emptyNode: <Empty className={styles.empty_box} />,
            }}
            style={{ height: 300, marginTop: 16 }}
            option={costTrendChartOption}
          />
        </div>
        <div className={styles.pieList}>
          {costTrendPieData?.list?.map((item, index) => (
            <div className={styles.item_pie} key={index}>
              <div className={styles.top}>{item.year ?? '--'}年度</div>
              <div className={styles.bottom}>
                <div className={styles.charts_wrapper}>
                  <EchartsWithTheme
                    empty={{
                      isShowEmpty: !item.year,
                      emptyNode: <Empty />,
                    }}
                    loading={trendPieLoading}
                    type="pie"
                    theme={theme}
                    style={{ height: 180 }}
                    option={getPieChartOption(item.energyMediumValueList)}
                  />
                </div>
                <div
                  className={styles.customer_legend}
                  style={{ justifyContent: (item.energyMediumValueList ?? []).length < 5 ? 'center' : undefined }}
                >
                  {(item.energyMediumValueList ?? []).map((item, index) => (
                    <>
                      <div className={styles.legend_item} key={index}>
                        <div className={styles.icon} style={{ backgroundColor: color[index] }}></div>
                        <div className={styles.label}>{formatName(item.energyMediumName)}</div>
                        <div className={styles.value}>{formatThousandUnitDefaultTwo(item.value)}元</div>
                      </div>
                      <div className={styles.rate}>{keepPercentOrLine(item.proportion)}%</div>
                    </>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default Cost;
